import { Form, Formik } from 'formik';
import { useState } from 'react';
import { RiCloseCircleLine } from 'react-icons/ri';
import * as Yup from 'yup';

import {
	CategoryError,
	CategoryMessage,
} from '@/exceptions/category/category.enum';
import useCreateCategory from '@/hooks/category/useCreateCategory';
import { ICategoryDto, Privacy } from '@/interfaces/entities/category';
import { notificationService } from '@/services/notification.service';

const CreateCategoryModal = ({ closeModal }: { closeModal: () => void }) => {
	const [loading, setLoading] = useState(false);
	const createCategory = useCreateCategory();
	const initialValues: ICategoryDto = {
		name: '',
		description: '',
		privacity: Privacy.PUBLIC,
	};

	const handleSubmit = async (values: ICategoryDto) => {
		const newCategory: ICategoryDto = {
			name: values.name,
			description: values.description,
			privacity: Privacy.PUBLIC,
		};
		setLoading(true);
		try {
			createCategory.mutate(newCategory);
			notificationService.success(CategoryMessage.CREATE_MESSAGE);
			closeModal();
		} catch (error) {
			notificationService.error(CategoryError.CREATE_ERROR);
		} finally {
			setLoading(false);
		}
	};

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(CategoryError.NAME_ERROR),
		description: Yup.string().required(CategoryError.DESCRIPTION_ERROR),
		privacity: Yup.string().required(CategoryError.PRIVACITY_ERROR),
	});
	return (
		<div data-test="create-category-modal" className="w-[100%] h-[100%]">
			<div className="flex justify-end pb-2">
				<button data-test="create-category-modal-close" onClick={closeModal}>
					<RiCloseCircleLine size={20} />
				</button>
			</div>

			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ handleChange, handleBlur, values, errors, touched }) => (
					<Form>
						<div
							className={`flex-1 justify-center items-center bg-gray-100 p-4`}
						>
							<h1 className={`text-2xl text-center font-bold mt-2 mb-2 `}>
								Create Category
							</h1>
							<div className="flex justify-start">
								<h2 className={`py-2 font-semibold`}>Name:</h2>
								<input
									className="w-full"
									data-test="create-name-input"
									type="text"
									name="name"
									onChange={handleChange('name')}
									value={values.name}
								/>
							</div>
							{touched.name && errors.name && (
								<p className={`text-red-500 text-sm`}>{errors.name}</p>
							)}
							<div className={`items-center justify-center`}>
								<div className={`w-full flex items-center justify-center`}>
									<h2 className={`py-2 font-semibold`}>Description</h2>
								</div>
								<textarea
									data-test="create-description-input"
									className={`w-full bg-white border border-gray-300 rounded-md p-2`}
									placeholder="Description"
									onChange={handleChange('description')}
									onBlur={handleBlur('description')}
									value={values.description}
									rows={5}
									style={{
										resize: 'none',
										overflowY: 'auto',
									}}
								/>
							</div>
							<div className="flex justify-center items-center w-full mt-4">
								<button
									data-test="create-button"
									className="bg-blue-600 hover:bg-blue-500 items-center justify-center px-3 py-1 rounded-md mx-4 ml-2 w-[25%] disabled:opacity-50"
									type="submit"
									disabled={loading}
								>
									<p className="text-white text-center">CREATE</p>
								</button>
							</div>
						</div>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default CreateCategoryModal;
